import { useState } from "react";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { HasAccess } from "@/components/common/has-access";
import { useSendPushNotification } from "../hooks/use-send-push-notification";

interface SendPushDialogProps {
  userId: string;
  userName?: string | null;
}

const DEFAULT_TYPE = "appointment_reminder";
const DEFAULT_PATH = "/appointments";

export function SendPushDialog({ userId, userName }: SendPushDialogProps) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [type, setType] = useState(DEFAULT_TYPE);
  const [path, setPath] = useState(DEFAULT_PATH);
  const [subscriptionId, setSubscriptionId] = useState("");

  const { mutate: sendPush, isPending } = useSendPushNotification();

  const reset = () => {
    setTitle("");
    setBody("");
    setType(DEFAULT_TYPE);
    setPath(DEFAULT_PATH);
    setSubscriptionId("");
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) reset();
  };

  const canSubmit =
    title.trim() && body.trim() && type.trim() && path.trim() && !isPending;

  const handleSubmit = () => {
    if (!canSubmit) return;
    sendPush(
      {
        user_id: userId,
        title: title.trim(),
        body: body.trim(),
        type: type.trim(),
        path: path.trim(),
        subscription_id: subscriptionId.trim() || undefined,
      },
      { onSuccess: () => handleOpenChange(false) },
    );
  };

  return (
    <HasAccess permission="write">
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            <Bell className="h-4 w-4 mr-1.5" />
            Send Push
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Send Push Notification{userName ? ` to ${userName}` : ""}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="push-title">Title</Label>
              <Input
                id="push-title"
                placeholder="Your session is tomorrow"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="push-body">Message</Label>
              <Textarea
                id="push-body"
                placeholder="Tap to view your appointment."
                value={body}
                onChange={(e) => setBody(e.target.value)}
                className="min-h-[80px]"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="push-type">Type</Label>
                <Input
                  id="push-type"
                  placeholder={DEFAULT_TYPE}
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="push-path">Deep-link path</Label>
                <Input
                  id="push-path"
                  placeholder={DEFAULT_PATH}
                  value={path}
                  onChange={(e) => setPath(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="push-subscription">
                Subscription ID{" "}
                <span className="text-xs text-gray-400">
                  (optional — testing override)
                </span>
              </Label>
              <Input
                id="push-subscription"
                placeholder="Send directly to a OneSignal subscription id"
                value={subscriptionId}
                onChange={(e) => setSubscriptionId(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button
              className="bg-orange-500 text-white hover:bg-orange-600"
              disabled={!canSubmit}
              onClick={handleSubmit}
            >
              {isPending ? "Sending..." : "Send Push"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </HasAccess>
  );
}
